import React, { type ReactNode } from 'react';

import { useIntl } from 'react-intl';

import { Box } from '@atlaskit/primitives/compiled/box';

export type SessionLastUpdatedProps = {
	/** Time of the latest state change, in epoch milliseconds. */
	lastStateChangeAt?: number;
	/** Compatibility for published consumers; new callers should use `lastStateChangeAt`. */
	lastUpdated?: ReactNode;
	testId?: string;
};

const RELATIVE_TIME_UNITS: ReadonlyArray<readonly [Intl.RelativeTimeFormatUnit, number]> = [
	['year', 31_536_000_000],
	['month', 2_592_000_000],
	['week', 604_800_000],
	['day', 86_400_000],
	['hour', 3_600_000],
	['minute', 60_000],
];

const RelativeSessionTime = ({ timestamp }: { timestamp: number }): React.JSX.Element => {
	const { locale } = useIntl();
	const now = React.useMemo(() => Date.now(), []);
	const formatter = React.useMemo(
		() => new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'narrow' }),
		[locale],
	);

	const difference = timestamp - now;
	const [unit, milliseconds] = RELATIVE_TIME_UNITS.find(
		([, unitMilliseconds]) => Math.abs(difference) >= unitMilliseconds,
	) ?? ['minute', 60_000];
	const relativeTime = formatter.format(Math.round(difference / milliseconds), unit);

	return <>{relativeTime}</>;
};

/** Renders a raw session timestamp as localized relative time. */
export const SessionLastUpdated = ({
	lastStateChangeAt,
	lastUpdated,
	testId,
}: SessionLastUpdatedProps): React.JSX.Element | null => {
	const content =
		lastStateChangeAt != null ? <RelativeSessionTime timestamp={lastStateChangeAt} /> : lastUpdated;

	if (content === undefined || content === null || content === false) {
		return null;
	}

	return (
		<Box as="span" testId={testId}>
			{content}
		</Box>
	);
};
